/**
 * Производное от контракта `./types.ts`: тела запросов, русские подписи кодов,
 * форматтеры чисел ИС, геометрия листа схемы (рамка, штамп, пересечения зон) и
 * пересчёт пикселей превью подложки в координаты листа.
 *
 * Числа здесь только ФОРМАТИРУЮТСЯ — считает их backend (ТЗ §10, П1). Исключение —
 * геометрия раскладки листа: проверку пересечений зон фронт делает сам, чтобы не
 * дать сохранить заведомо невалидную раскладку (ГОСТ Р 51872 прил. В).
 */
import type {
  GeoAction,
  GeoCodeRule,
  GeoColumnOrder,
  GeoContourMode,
  GeoDesignElement,
  GeoElementDeviation,
  GeoElementKind,
  GeoEvent,
  GeoOrientation,
  GeoPileLayout,
  GeoPointRole,
  GeoRect,
  GeoRejectedBy,
  GeoSchemeListItem,
  GeoSchemeStatus,
  GeoSettings,
  GeoSheetFormat,
  GeoSheetRole,
  GeoSheetSource,
  GeoSnapPoint,
  GeoStampSuggestion,
  GeoWorkType,
  GeoWorkTypeInfo,
  GeoZones,
} from './types';

/** Тон бейджа — набор значений UI-примитива `@shared/ui/Badge`. */
export type GeoTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/* ── Тела и ответы API (сверх контракта карточки) ───────────────────────────── */

/** Модуль агента в карточке `/info` (форма свободная — показываем как есть). */
export interface GeoInfoModule {
  code: string;
  title?: string;
  status?: string;
}

/** Карточка агента `GET /agents/geodesy/info`. */
export interface GeoInfo {
  agent_code: string;
  title: string;
  description: string;
  modules: GeoInfoModule[];
}

export interface GeoSchemeListParams {
  status?: GeoSchemeStatus;
  work_type?: GeoWorkType;
  project_id?: string;
  site_id?: string;
  element_id?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface GeoSchemeListResponse {
  schemes: GeoSchemeListItem[];
  total: number;
}

export interface GeoSchemeCreateBody {
  title: string;
  work_type: GeoWorkType;
  project_id?: string;
  site_id?: string;
  element_id?: string;
  request_id?: string;
  object_name?: string;
}

/** `PATCH /schemes/{id}`: `settings` передаются ЦЕЛИКОМ; `base_settings` — от
 *  каких настроек отталкивалась правка: сервер возьмёт из неё только разделы,
 *  которые она меняет (правки другой вкладки в остальных не затрутся). */
export interface GeoSchemePatchBody {
  title?: string;
  work_type?: GeoWorkType;
  object_name?: string;
  project_id?: string;
  site_id?: string;
  element_id?: string | null;
  settings?: GeoSettings;
  base_settings?: GeoSettings;
}

/** Элементы-точки листа: сваи PDF или объекты DXF по слоям/блокам. */
export type GeoEntitiesMode = 'piles_pdf' | 'dxf_entities';
/** `outlines` — контуры конструкций (бетон) у точек съёмки, PDF и DXF. */
export type GeoExtractMode = GeoEntitiesMode | 'outlines';

export interface GeoExtractBody {
  mode: GeoExtractMode;
  layers?: string[];
  blocks?: string[];
  circles?: boolean;
  points?: boolean;
  kind?: GeoElementKind;
  size_mm?: number | null;
  /** Режим `outlines`: проектная отметка верха всех найденных контуров, м. */
  h_top?: number | null;
  /** Режим `piles_pdf`: какая отметка таблицы листа — проектная отметка головы. */
  pile_level?: GeoPileLevel;
}

/** Проектная отметка головы сваи из таблицы листа: после срубки или до неё. */
export type GeoPileLevel = 'head_cut' | 'head_driven';
export const PILE_LEVELS: { value: GeoPileLevel; label: string }[] = [
  { value: 'head_cut', label: 'верх сваи после срубки' },
  { value: 'head_driven', label: 'верх сваи после забивки (до срубки)' },
];

/** Выгрузка для выноса в натуру: DXF или DWG 2010 для AutoCAD, каталог для прибора и Excel. */
export type GeoDesignExportFormat = 'dxf' | 'dwg' | 'txt' | 'csv';

/* ── Листы комплекта и сборка из листов (ТЗ AI-GEO-2026 v2.1) ───────────────── */

/** Роли листа комплекта РД — подписи для выбора и карточки. */
export const SHEET_ROLES: ReadonlyArray<{ value: GeoSheetRole; label: string; hint: string }> = [
  {
    value: 'layout_plan',
    label: 'Схема расположения',
    hint: 'оси, координаты МСК, отметка низа, спецификация к схеме',
  },
  {
    value: 'structural_detail',
    label: 'Чертёж конструкции',
    hint: 'опалубка марки: подошва, подколонник, ниши, болты, разрез с высотами',
  },
  { value: 'anchor_detail', label: 'Анкерные блоки, закладные', hint: 'шаги и диаметры болтов' },
  { value: 'isometric', label: 'Изометрия', hint: 'узлы трубопровода с координатами и EL' },
];

export function sheetRoleLabel(role: GeoSheetRole | null | undefined): string {
  return SHEET_ROLES.find((r) => r.value === role)?.label ?? 'роль не узнана';
}

/** Правка листа комплекта: страница (у основного — нельзя) и роль (`null` — найти заново). */
export interface GeoSheetPatchBody {
  page?: number;
  role?: GeoSheetRole | null;
}

/** Сборка конструкции из листов: что геодезист вписал сам (сильнее найденного на листах). */
export interface GeoAssembleBody {
  /** Марка («Рсм12»); пусто — марки, у которых снята съёмка (без съёмки — все). */
  mark?: string | null;
  /** Отметка низа от 0,000, м — если её нет ни на схеме, ни на разрезе. */
  base_level?: number | null;
  /** Абсолютная отметка 0,000 (БС), м — если её нет в примечаниях листов. */
  zero_bs?: number | null;
}

/** Отметки ярусов сборки: имя от backend → подпись. */
export const ASSEMBLY_LEVELS: Record<string, string> = {
  base: 'низ',
  preparation_bottom: 'низ подготовки',
  sole_top: 'верх подошвы',
  step_top: 'верх ступени',
  pedestal_top: 'верх подколонника',
  recess_bottom: 'дно ниши',
  bolt_top: 'верх болтов',
};

/**
 * Узлы изометрий комплекта с координатами (`GET /schemes/{id}/iso-nodes`) — центр
 * для привязки листа одной конструкции («строительный лист ↔ изометрия»).
 */
export interface GeoIsoNode {
  file_id: string;
  file_name: string;
  designation: string | null;
  /** Подписи у узла — марки и шифры листов («Оп-3», «GA-0003»). */
  labels: string[];
  /** Как на листе: «B= 18169.358 A= 15580.361 EL +2.150». */
  text: string;
  /** X (север), Y (восток), отметка узла, м. */
  x: number;
  y: number;
  h: number | null;
}
export interface GeoIsoNodes {
  nodes: GeoIsoNode[];
  warnings: string[];
}

const LATIN_TWINS = 'ABCEHKMOPTXY';
const CYRILLIC_TWINS = 'АВСЕНКМОРТХУ';

/**
 * Узел изометрии с маркой конструкции у выноски; нет такого — null (узел выбирает геодезист).
 * Марки сравниваются без пробелов, точек, дефисов и регистра, латиница-двойник — как кириллица.
 */
export function isoNodeFor(nodes: GeoIsoNode[], mark: string | null): GeoIsoNode | null {
  const key = (s: string) =>
    s
      .replace(/[\s.-]/g, '')
      .toUpperCase()
      .replace(/[ABCEHKMOPTXY]/g, (c) => CYRILLIC_TWINS[LATIN_TWINS.indexOf(c)]);
  if (mark) {
    const k = key(mark);
    const hit = nodes.find((n) => n.labels.some((l) => key(l) === k));
    if (hit) return hit;
  }
  // Узел без марки, даже единственный, — не центр: это ось трубопровода, а
  // конструкция может стоять в стороне (Фм1 на ISO-0001 — в 584 мм от стояка).
  return null;
}

/** Как найден центр марки при сборке из листов; пусто — сборка до сверки по размерам. */
export function centerByLabel(by: string | undefined): string {
  switch (by) {
    case 'both':
      return 'по схеме, размеры от осей сошлись';
    case 'dims':
      return 'по размерам от осей';
    case 'drawing':
      return 'по контуру на схеме';
    case 'sheet':
      return 'по привязке листа марки';
    default:
      return '';
  }
}

/** Как найден поворот марки на схеме. */
export function thetaByLabel(by: string): string {
  switch (by) {
    case 'marker':
      return 'по знаку ориентации ▼';
    case 'inner':
      return 'по подколонникам на схеме';
    case 'survey':
      return 'по точкам съёмки';
    case 'size':
      return 'по габариту';
    default:
      return 'не определён — проверьте';
  }
}

export type GeoSurveyUploadMode = 'replace' | 'merge';
/** Поле формы `assist` загрузки съёмки: `auto` — ИИ-помощник разбирает нестандартный файл. */
export type GeoSurveyAssist = 'auto' | 'off';
/** `dwg` — DWG 2010, собирается сервером из DXF версии при скачивании. */
export type GeoVersionFileKind = 'dxf' | 'dwg' | 'pdf' | 'png';

/** `GET /schemes/{id}/sheet/snaps`. */
export interface GeoSnapsResponse {
  points: GeoSnapPoint[];
  warnings: string[];
}

/** `GET /schemes/{id}/sheet/axes`: оси листа с марками в кружках и их пересечения. */
export interface GeoSheetAxes {
  /** Марки осей-цифр («1», «3/1») и осей-букв («А») — по порядку сетки. */
  digits: string[];
  letters: string[];
  /** Пересечения «1-А» в координатах листа (y вверх). */
  crosses: { name: string; u: number; v: number }[];
  warnings: string[];
}

/** Документ РД объекта с действующей редакцией и файлами листов (реестр ПТО). */
export interface GeoRdDocument {
  document_id: string;
  code: string;
  mark: string | null;
  title: string;
  revision_id: string;
  revision_no: number;
  rev_label: string | null;
  uploaded_at: string;
  files: string[];
}

/** `GET /schemes/{id}/rd/documents`: реестр РД объекта ИС; ИС без объекта — `project: null`. */
export interface GeoRdDocuments {
  project: { id: string; name: string } | null;
  documents: GeoRdDocument[];
}

/** `GET /schemes/{id}/sheet/coords`: выноски «X=…, Y=…» листа — острие стрелки и X, Y. */
export interface GeoSheetCoords {
  /** `label` — имя точки привязки («в1»), `text` — как на листе («Х=98519,62 Y=69065,30»). */
  callouts: { label: string; text: string; u: number; v: number; x: number; y: number }[];
  warnings: string[];
}

/** План конструкции на листе одной конструкции (опалубочный чертёж). */
export interface GeoStructureView {
  /** Марка («Рсм4») и заголовок вида. */
  label: string | null;
  title: string | null;
  /** Контур плана, координаты листа (y вверх). */
  outline: [number, number][];
  circle: [number, number, number] | null;
  /** Точка привязки на листе: `axes` — пересечение осей конструкции, `box` — центр габарита. */
  center: [number, number];
  center_by: 'axes' | 'box' | 'circle';
  angle_deg: number;
  /** Масштаб плана 1:N и метров натуры в единице листа — по размерам. */
  scale: number | null;
  unit_m: number | null;
  /** Сколько размерных чисел подтвердили масштаб. */
  dims: number;
  size_m: [number, number] | null;
}

/** `GET /schemes/{id}/sheet/structure`: лист одной конструкции. */
export interface GeoSheetStructure {
  structures: GeoStructureView[];
  warnings: string[];
}

/** `POST /schemes/{id}/binding/structure`: где на площадке точка привязки и как повёрнут план. */
export interface GeoStructureBindBody {
  u: number;
  v: number;
  unit_m: number;
  position:
    | {
        kind: 'point';
        x: number;
        y: number;
        name?: string | null;
        source?: 'catalog' | 'iso' | null;
      }
    | { kind: 'cross'; grid_id: string; a: string; b: string };
  rotation: { grid_id?: string | null; azimuth_deg?: number | null; quarter?: number | null };
  outline: [number, number][];
  label: string | null;
  scale: number | null;
}

/** `POST /schemes/{id}/binding/axes`: пересечения осей и их X, Y (разбивочный план). */
export interface GeoAxesBindBody {
  points: { a: string; b: string; x: number; y: number }[];
  /** Запомнить сошедшиеся пересечения для объекта ИС — в сетку здания с этим названием. */
  grid?: string;
}

/** Пересечение осей объекта: «1-А», X (север) и Y (восток), м. */
export interface GeoGridPoint {
  name: string;
  x: number;
  y: number;
}

/** Сетка осей здания на объекте ИС. */
export interface GeoAxisGrid {
  id: string;
  /** Здание или сооружение: «Корпус 1». */
  name: string;
  points: GeoGridPoint[];
  updated_by_name: string | null;
  updated_at: string;
}

/** `GET /schemes/{id}/axis-grids`: оси объекта ИС; ИС без объекта — `project: null`. */
export interface GeoAxisGrids {
  project: { id: string; name: string } | null;
  grids: GeoAxisGrid[];
}

/** `POST /schemes/{id}/binding/auto`: без слоёв/блоков сервер ищет по всему листу. */
export interface GeoAutoBindBody {
  mode?: GeoEntitiesMode;
  layers?: string[];
  blocks?: string[];
}

/** Строка `PUT /schemes/{id}/survey/codes`. */
export type GeoCodeRuleInput = Pick<GeoCodeRule, 'code' | 'role'>;

export interface GeoTolerancesResponse {
  work_types: GeoWorkTypeInfo[];
}

/* ── Типы работ ─────────────────────────────────────────────────────────────── */

/** Порядок — как в регламенте камеральщика (КЖ, КМ, насыпь, выемка, сваи). */
export const WORK_TYPES: ReadonlyArray<{ code: GeoWorkType; short: string; title: string }> = [
  { code: 'kzh', short: 'КЖ', title: 'Монолитные железобетонные конструкции' },
  { code: 'km', short: 'КМ', title: 'Металлические конструкции' },
  { code: 'fill', short: 'Насыпь', title: 'Насыпь, спланированная поверхность' },
  { code: 'cut', short: 'Выемка', title: 'Выемка, дно котлована' },
  { code: 'piles', short: 'Сваи', title: 'Свайное поле' },
];

export function workTypeShort(code: GeoWorkType): string {
  return WORK_TYPES.find((w) => w.code === code)?.short ?? code;
}

export function workTypeTitle(code: GeoWorkType): string {
  return WORK_TYPES.find((w) => w.code === code)?.title ?? code;
}

/* ── Статусы и переходы (ТЗ §3.4) ──────────────────────────────────────────── */

export const SCHEME_STATUSES: ReadonlyArray<{ value: GeoSchemeStatus; label: string }> = [
  { value: 'draft', label: 'Черновик' },
  { value: 'generated', label: 'Сформирована' },
  { value: 'submitted', label: 'На проверке в ПТО' },
  { value: 'accepted', label: 'Принята ПТО' },
  { value: 'sent_to_customer', label: 'У заказчика' },
  { value: 'approved', label: 'Согласована заказчиком' },
  { value: 'rejected', label: 'Отклонена' },
];

export function isSchemeStatus(value: string | null | undefined): value is GeoSchemeStatus {
  return SCHEME_STATUSES.some((s) => s.value === value);
}

export function isWorkType(value: string | null | undefined): value is GeoWorkType {
  return WORK_TYPES.some((w) => w.code === value);
}

export function schemeStatusLabel(
  status: GeoSchemeStatus,
  rejectedBy?: GeoRejectedBy | null,
): string {
  if (status === 'rejected') {
    if (rejectedBy === 'pto') return 'Отклонена ПТО';
    if (rejectedBy === 'customer') return 'Отклонена заказчиком';
  }
  return SCHEME_STATUSES.find((s) => s.value === status)?.label ?? status;
}

export function schemeStatusTone(status: GeoSchemeStatus): GeoTone {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'danger';
    case 'submitted':
      return 'warning';
    case 'generated':
    case 'accepted':
    case 'sent_to_customer':
      return 'info';
    default:
      return 'neutral';
  }
}

/** Подпись статуса из журнала (строка с бэкенда; неизвестное — как есть). */
export function anyStatusLabel(status: string): string {
  return SCHEME_STATUSES.find((s) => s.value === status)?.label ?? status;
}

export interface GeoActionSpec {
  action: GeoAction;
  label: string;
  /** Кто выполняет: геодезист (`can_edit`) или инженер ПТО (`can_review`). */
  actor: 'geodesist' | 'engineer';
  /** Из каких статусов действие допустимо (зеркало автомата backend). */
  from: ReadonlyArray<GeoSchemeStatus>;
  needsReason: boolean;
  variant: 'primary' | 'secondary' | 'danger';
  /** Пояснение в диалоге подтверждения. */
  hint: string;
}

export const GEO_ACTIONS: Readonly<Record<GeoAction, GeoActionSpec>> = {
  submit: {
    action: 'submit',
    label: 'Передать в ОСНОВА.Инженер',
    actor: 'geodesist',
    from: ['generated'],
    needsReason: false,
    variant: 'primary',
    hint: 'Схема уйдёт инженеру ПТО на проверку. Связанная заявка получит результат и перейдёт в «выполнена».',
  },
  accept: {
    action: 'accept',
    label: 'Принять',
    actor: 'engineer',
    from: ['submitted'],
    needsReason: false,
    variant: 'primary',
    hint: 'Схема проверена и принята ПТО.',
  },
  reject: {
    action: 'reject',
    label: 'Отклонить',
    actor: 'engineer',
    from: ['submitted', 'accepted'],
    needsReason: true,
    variant: 'danger',
    hint: 'Геодезист увидит причину и выпустит исправленную версию.',
  },
  send_to_customer: {
    action: 'send_to_customer',
    label: 'Передано заказчику',
    actor: 'engineer',
    from: ['accepted'],
    needsReason: false,
    variant: 'secondary',
    hint: 'Отметка, что схема передана заказчику на согласование.',
  },
  customer_reject: {
    action: 'customer_reject',
    label: 'Отклонена заказчиком',
    actor: 'engineer',
    from: ['sent_to_customer'],
    needsReason: true,
    variant: 'danger',
    hint: 'Замечания заказчика уйдут геодезисту — он скорректирует схему.',
  },
  approve: {
    action: 'approve',
    label: 'Согласована заказчиком',
    actor: 'engineer',
    from: ['sent_to_customer'],
    needsReason: false,
    variant: 'primary',
    hint: 'Итоговое согласование: схема готова к подшивке в исполнительную документацию.',
  },
};

/** Действия, доступные по статусу и правам. Окончательная проверка — на backend. */
export function availableActions(
  status: GeoSchemeStatus,
  perms: { can_edit: boolean; can_review: boolean },
): GeoActionSpec[] {
  return Object.values(GEO_ACTIONS).filter((spec) => {
    if (!spec.from.includes(status)) return false;
    return spec.actor === 'geodesist' ? perms.can_edit : perms.can_review;
  });
}

/* ── Подписи справочных кодов ──────────────────────────────────────────────── */

export const PILE_LAYOUTS: ReadonlyArray<{ value: GeoPileLayout; label: string; hint: string }> = [
  { value: 'single_row', label: 'Однорядное', hint: 'поперёк ряда ±0,2d, вдоль ±0,3d' },
  {
    value: 'cluster',
    label: 'Кусты и ленты в 2–3 ряда',
    hint: 'крайние поперёк ±0,2d, прочие ±0,3d',
  },
  { value: 'field', label: 'Сплошное поле', hint: 'крайние ±0,2d, средние ±0,4d' },
  { value: 'single', label: 'Одиночные', hint: '±50 мм' },
  { value: 'pile_column', label: 'Сваи-колонны', hint: '±30 мм' },
  {
    value: 'large_diameter',
    label: 'Диаметр больше 0,5 м',
    hint: 'поперёк ±100 мм, вдоль ±150 мм',
  },
];

export const ELEMENT_KINDS: ReadonlyArray<{ value: GeoElementKind; label: string }> = [
  { value: 'pile', label: 'Свая' },
  { value: 'column', label: 'Колонна' },
  { value: 'point', label: 'Точка' },
  { value: 'foundation', label: 'Фундамент' },
  { value: 'surface', label: 'Поверхность' },
  { value: 'anchor', label: 'Анкерный болт' },
];

export function elementKindLabel(kind: GeoElementKind): string {
  return ELEMENT_KINDS.find((k) => k.value === kind)?.label ?? kind;
}

/** Как читать проектные точки (шаг 7): элементы или углы контуров. */
export const CONTOUR_MODES: ReadonlyArray<{ value: GeoContourMode; label: string; hint: string }> =
  [
    {
      value: 'auto',
      label: 'Авто',
      hint: 'КЖ: если каталог целиком раскладывается на прямоугольники со сторонами до 6 м — это углы ростверков (фундаментов), отклонения считаются по граням. Иначе — отдельные элементы.',
    },
    {
      value: 'points',
      label: 'Отдельные элементы (сваи, колонны)',
      hint: 'Каждая проектная точка — свой элемент: отклонение центра в плане и отметка.',
    },
    {
      value: 'corners',
      label: 'Углы контуров (ростверки, фундаменты)',
      hint: 'Каждые четыре угла прямоугольника — один контур. У каждого угла — смещения двух граней по точкам на гранях и отметка верха.',
    },
  ];

/** Подпись строки отчёта: у угла контура — с маркой («Р3 (17)»). */
export function deviationItemLabel(d: Pick<GeoElementDeviation, 'label' | 'contour'>): string {
  if (!d.contour || d.label.startsWith(d.contour)) return d.label;
  return `${d.contour} (${d.label})`;
}

/** Подпись проектного элемента: у угла контура — с маркой («Фм-1 (3)»). */
export function designElementLabel(el: Pick<GeoDesignElement, 'label' | 'contour'>): string {
  if (!el.contour || el.label.startsWith(el.contour)) return el.label;
  return `${el.contour} (${el.label})`;
}

/** Круглая конструкция (один элемент-центр с маркой контура и диаметром). */
export function isRoundElement(
  el: Pick<GeoDesignElement, 'contour' | 'shape' | 'size_mm'>,
): boolean {
  return el.contour != null && el.shape === 'circle' && (el.size_mm ?? 0) > 0;
}

/** Откуда взят проектный элемент (код источника — backend; неизвестный — как есть). */
export function designSourceLabel(source: string): string {
  const s = source.toLowerCase();
  if (s === 'manual') return 'вручную';
  if (s === 'outline') return 'контур с листа';
  if (s === 'assembly') return 'сборка из листов';
  if (s.includes('pdf')) return 'из PDF-листа';
  if (s.includes('dxf')) return 'из DXF-листа';
  if (s.includes('catalog') || s.includes('import') || s.includes('csv') || s.includes('xls'))
    return 'каталог';
  return source;
}

/**
 * Роли точки съёмки. `label` — для точки (таблица, правка строки), `codeLabel` —
 * для правила «код → роль». У `single` верх/низ всё равно определяются по имени
 * точки (12в / 12н) — это сказано в подсказке таблицы кодов: в выпадающем
 * списке длинная подпись обрезалась бы посреди слова.
 */
export const SURVEY_ROLES: ReadonlyArray<{
  value: GeoPointRole;
  label: string;
  codeLabel: string;
}> = [
  { value: 'single', label: 'Точка элемента', codeLabel: 'Точка элемента' },
  { value: 'top', label: 'Верх (В)', codeLabel: 'Верх (В)' },
  { value: 'bottom', label: 'Низ (Н)', codeLabel: 'Низ (Н)' },
  { value: 'ignore', label: 'Не элемент', codeLabel: 'Не элемент — репер, станция' },
];

export function surveyRoleLabel(role: GeoPointRole): string {
  return SURVEY_ROLES.find((r) => r.value === role)?.label ?? role;
}

/** Значение `<select>` → роль (чужое значение — `null`). */
export function parsePointRole(value: string): GeoPointRole | null {
  return SURVEY_ROLES.find((r) => r.value === value)?.value ?? null;
}

/** Откуда роль у кода точки. `llm` в интерфейсе — бейдж «ИИ-помощник». */
export function codeRuleSourceLabel(source: GeoCodeRule['source']): string {
  switch (source) {
    case 'rule':
      return 'словарь';
    case 'llm':
      return 'ИИ-помощник';
    case 'user':
      return 'вручную';
    case 'default':
      return 'по умолчанию';
  }
}

export const COLUMN_ORDERS: ReadonlyArray<{ value: GeoColumnOrder; label: string; hint: string }> =
  [
    { value: 'nxyhc', label: 'N · X · Y · H · код (PNEZD)', hint: 'по умолчанию' },
    { value: 'nyxhc', label: 'N · Y · X · H · код', hint: 'восток раньше севера' },
  ];

export function matchedByLabel(m: GeoElementDeviation['matched_by']): string {
  switch (m) {
    case 'name':
      return 'по имени';
    case 'nearest':
      return 'ближайшая';
    case 'manual':
      return 'вручную';
    case 'surface':
      return 'по поверхности проекта';
    case 'faces':
      return 'по граням';
    case 'circle':
      return 'по окружности';
    default:
      return '—';
  }
}

/**
 * Строка отчёта — точка съёмки на проектной поверхности (насыпь/выемка, `sv:<имя>`),
 * а не конструкция (`e:<метка>`): ручное сопоставление с другой точкой ей не нужно.
 */
export function isSurfaceItem(d: Pick<GeoElementDeviation, 'element_id' | 'matched_by'>): boolean {
  return d.matched_by === 'surface' || d.element_id.startsWith('sv:');
}

export function deviationStatusLabel(s: GeoElementDeviation['status']): string {
  switch (s) {
    case 'ok':
      return 'в допуске';
    case 'exceeded':
      return 'превышение';
    case 'not_measured':
      return 'не измерен';
    case 'excluded':
      return 'исключён';
  }
}

export function deviationStatusTone(s: GeoElementDeviation['status']): GeoTone {
  switch (s) {
    case 'ok':
      return 'success';
    case 'exceeded':
      return 'danger';
    case 'not_measured':
      return 'warning';
    case 'excluded':
      return 'neutral';
  }
}

/** Вид съёмки в заявке (`payload.survey_type`; коды — как в мобильном «Прорабе»). */
export const SURVEY_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'executive', label: 'Исполнительная' },
  { value: 'control', label: 'Контрольная' },
  { value: 'layout', label: 'Разбивка' },
];

export function surveyTypeLabel(code: string | null): string | null {
  if (!code) return null;
  return SURVEY_TYPES.find((t) => t.value === code)?.label ?? code;
}

/** Вид съёмки как самостоятельная фраза: «Исполнительная съёмка», «Разбивка». */
export function surveyTypePhrase(code: string | null): string | null {
  switch (code) {
    case null:
    case '':
      return null;
    case 'executive':
      return 'Исполнительная съёмка';
    case 'control':
      return 'Контрольная съёмка';
    case 'layout':
      return 'Разбивка';
    default:
      return code;
  }
}

/** Функция-инициатор заявки — по-русски (сырые коды в интерфейс не выводим). */
export function sourceFunctionLabel(code: string): string {
  switch (code) {
    case 'field':
      return 'Прораб';
    case 'pto':
      return 'Инженер ПТО';
    default:
      return code;
  }
}

/** Заголовок события журнала. Коды событий — backend; неизвестное без сырого кода. */
export function eventTitle(e: GeoEvent): string {
  if (e.to_status) {
    const to = anyStatusLabel(e.to_status);
    return e.from_status ? `${anyStatusLabel(e.from_status)} → ${to}` : `Статус: ${to}`;
  }
  const k = e.kind.toLowerCase();
  if (k.includes('comment')) return 'Комментарий';
  if (k.includes('creat')) return 'Схема создана';
  if (k.includes('upload') && k.includes('version')) return 'Загружена версия из CAD';
  if (k.includes('generat')) return 'Сформирована версия';
  if (k.includes('survey')) return 'Изменена съёмка';
  if (k.includes('design')) return 'Изменены проектные элементы';
  if (k.includes('override') || k.includes('deviation') || k.includes('correct'))
    return 'Корректировка отклонений';
  if (k.includes('comput')) return 'Рассчитаны отклонения';
  if (k.includes('sheet')) return 'Проектный лист';
  return 'Изменение схемы';
}

/* ── Форматирование ────────────────────────────────────────────────────────── */

const MINUS = '−';

/** Координата/отметка, м: без разрядных пробелов (так их пишут геодезисты), запятая. */
export function fmtCoord(v: number | null | undefined, digits = 3): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v
    .toLocaleString('ru-RU', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      useGrouping: false,
    })
    .replace('-', MINUS);
}

/** Отклонение в мм со знаком: «+5», «−3», «0» (ГОСТ Р 51872 Б.3). */
export function fmtMm(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const n = Math.round(v);
  if (n === 0) return '0';
  return n > 0 ? `+${n}` : `${MINUS}${Math.abs(n)}`;
}

/** Модуль в мм без знака. */
export function fmtMmAbs(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return String(Math.round(Math.abs(v)));
}

/** Допуск «±20» или «+5 / −10»; оба пусто — прочерк. */
export function fmtTol(plus: number | null | undefined, minus?: number | null): string {
  const p = plus == null ? null : Math.abs(Math.round(plus));
  const m = minus == null ? null : Math.abs(Math.round(minus));
  if (p == null && m == null) return '—';
  if (m == null || p === m) return `±${p ?? m}`;
  if (p == null) return `${MINUS}${m}`;
  return `+${p} / ${MINUS}${m}`;
}

/** Расстояние в метрах (невязки, радиус сопоставления). */
export function fmtMeters(v: number | null | undefined, digits = 3): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${fmtCoord(v, digits)} м`;
}

export function fmtNumber(v: number | null | undefined, maxDigits = 2): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toLocaleString('ru-RU', { maximumFractionDigits: maxDigits }).replace('-', MINUS);
}

export function fmtScale(denominator: number | null | undefined): string {
  if (denominator == null || !Number.isFinite(denominator)) return '—';
  return `1:${Math.round(denominator)}`;
}

export function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Дата `YYYY-MM-DD` (или ISO) → `09.10.2026`. Дату без времени не гоняем через
 *  `Date` — иначе в западных часовых поясах она съезжает на сутки назад. */
export function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Имя файла версии: `ИС_<номер>_v<n>.<ext>`. */
export function versionFileName(number: string, version: number, ext: GeoVersionFileKind): string {
  const safe = number.replace(/[\\/:*?"<>|\s]+/g, '_');
  return `ИС_${safe}_v${version}.${ext}`;
}

/* ── Ввод чисел ────────────────────────────────────────────────────────────── */

/** Число из поля ввода: запятая или точка, пробелы игнорируются. Пусто/мусор → null. */
export function parseDecimal(raw: string): number | null {
  const s = raw
    .replace(/[\s  ]/g, '')
    .replace(MINUS, '-')
    .replace(',', '.');
  if (s === '' || s === '-' || s === '.') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Число → строка для поля ввода (полная точность, запятая). */
export function numToInput(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '';
  return String(v).replace('.', ',');
}

/* ── Ошибки API ────────────────────────────────────────────────────────────── */

/** Текст ошибки бэкенда `{"error":{"code","message"}}` из ошибки RTK Query. */
export function apiErrorMessage(err: unknown): string | null {
  if (typeof err !== 'object' || err === null || !('data' in err)) return null;
  const data = err.data;
  if (typeof data !== 'object' || data === null || !('error' in data)) return null;
  const inner = data.error;
  if (typeof inner === 'string') return inner.trim() || null;
  if (typeof inner !== 'object' || inner === null || !('message' in inner)) return null;
  const message = inner.message;
  return typeof message === 'string' && message.trim() ? message : null;
}

/** Строковое поле произвольного JSON (payload заявки). */
export function readString(obj: unknown, key: string): string | null {
  if (typeof obj !== 'object' || obj === null) return null;
  const value: unknown = Reflect.get(obj, key);
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/* ── Лист схемы: формат, рамка, штамп (ГОСТ Р 21.101) ─────────────────────── */

const SHEET_SIDES_MM: Readonly<Record<GeoSheetFormat, readonly [number, number]>> = {
  A4: [210, 297],
  A3: [297, 420],
  A2: [420, 594],
  A1: [594, 841],
  A0: [841, 1189],
};

export const SHEET_FORMATS: ReadonlyArray<GeoSheetFormat> = ['A4', 'A3', 'A2', 'A1', 'A0'];

export interface SheetSize {
  w: number;
  h: number;
}

export function sheetSize(format: GeoSheetFormat, orientation: GeoOrientation): SheetSize {
  const [short, long] = SHEET_SIDES_MM[format];
  return orientation === 'landscape' ? { w: long, h: short } : { w: short, h: long };
}

/** Поле подшивки слева 20 мм, прочие поля 5 мм. */
export const FRAME_MARGINS = { left: 20, right: 5, top: 5, bottom: 5 } as const;
/** Основная надпись по форме 3 — 185×55 мм в правом нижнем углу рамки. */
export const STAMP_SIZE = { w: 185, h: 55 } as const;
/** Шаг привязки зон к сетке, мм. */
export const LAYOUT_GRID_MM = 5;
/** Минимальный размер зоны, мм. */
export const ZONE_MIN = { w: 20, h: 15 } as const;

export function frameRect(size: SheetSize): GeoRect {
  return {
    x: FRAME_MARGINS.left,
    y: FRAME_MARGINS.bottom,
    w: size.w - FRAME_MARGINS.left - FRAME_MARGINS.right,
    h: size.h - FRAME_MARGINS.top - FRAME_MARGINS.bottom,
  };
}

export function stampRect(size: SheetSize): GeoRect {
  return {
    x: size.w - FRAME_MARGINS.right - STAMP_SIZE.w,
    y: FRAME_MARGINS.bottom,
    w: STAMP_SIZE.w,
    h: STAMP_SIZE.h,
  };
}

const EPS = 0.01;

/** Пересечение с ненулевой площадью (касание краями — не пересечение). */
export function rectsOverlap(a: GeoRect, b: GeoRect): boolean {
  return (
    a.x < b.x + b.w - EPS && b.x < a.x + a.w - EPS && a.y < b.y + b.h - EPS && b.y < a.y + a.h - EPS
  );
}

export function rectInside(inner: GeoRect, outer: GeoRect): boolean {
  return (
    inner.x >= outer.x - EPS &&
    inner.y >= outer.y - EPS &&
    inner.x + inner.w <= outer.x + outer.w + EPS &&
    inner.y + inner.h <= outer.y + outer.h + EPS
  );
}

export const ZONE_KEYS = [
  'drawing',
  'situation',
  'legend',
  'notes',
  'tolerances',
  'catalog',
] as const;
export type GeoZoneKey = (typeof ZONE_KEYS)[number];

export const ZONE_LABELS: Readonly<Record<GeoZoneKey, string>> = {
  drawing: 'Чертёж',
  situation: 'Ситуационный план',
  legend: 'Условные обозначения',
  notes: 'Примечания',
  tolerances: 'Допуски',
  catalog: 'Каталог координат',
};

/** Размер новой зоны при включении (мм). */
export const ZONE_DEFAULT_SIZE: Readonly<Record<GeoZoneKey, { w: number; h: number }>> = {
  drawing: { w: 200, h: 150 },
  situation: { w: 70, h: 55 },
  legend: { w: 90, h: 50 },
  notes: { w: 120, h: 45 },
  tolerances: { w: 120, h: 40 },
  catalog: { w: 110, h: 80 },
};

export function zoneRect(zones: GeoZones, key: GeoZoneKey): GeoRect | null {
  return key === 'drawing' ? zones.drawing : zones[key];
}

export interface LayoutProblem {
  /** Зоны, к которым относится проблема (для подсветки). */
  zones: GeoZoneKey[];
  /** Задета ли основная надпись. */
  stamp: boolean;
  message: string;
}

/** Пересечения зон между собой и со штампом + выход за рамку. */
export function layoutProblems(zones: GeoZones, size: SheetSize): LayoutProblem[] {
  const frame = frameRect(size);
  const stamp = stampRect(size);
  const present = ZONE_KEYS.flatMap((key) => {
    const r = zoneRect(zones, key);
    return r ? [{ key, r }] : [];
  });
  const problems: LayoutProblem[] = [];
  for (const { key, r } of present) {
    if (!rectInside(r, frame)) {
      problems.push({
        zones: [key],
        stamp: false,
        message: `«${ZONE_LABELS[key]}» выходит за рамку`,
      });
    }
    if (rectsOverlap(r, stamp)) {
      problems.push({
        zones: [key],
        stamp: true,
        message: `«${ZONE_LABELS[key]}» заходит на основную надпись`,
      });
    }
  }
  for (let i = 0; i < present.length; i += 1) {
    for (let j = i + 1; j < present.length; j += 1) {
      const a = present[i];
      const b = present[j];
      if (a && b && rectsOverlap(a.r, b.r)) {
        problems.push({
          zones: [a.key, b.key],
          stamp: false,
          message: `«${ZONE_LABELS[a.key]}» пересекается с «${ZONE_LABELS[b.key]}»`,
        });
      }
    }
  }
  return problems;
}

export function snapMm(v: number, grid = LAYOUT_GRID_MM): number {
  return Math.round(v / grid) * grid;
}

/**
 * Свободное место под новую зону: перебор узлов сетки рамки сверху вниз, справа
 * налево (свободное поле обычно правее и выше чертежа). Нет места — `null`.
 */
export function findFreeSpot(
  zones: GeoZones,
  size: SheetSize,
  want: { w: number; h: number },
): GeoRect | null {
  const frame = frameRect(size);
  const stamp = stampRect(size);
  const others = ZONE_KEYS.flatMap((k) => {
    const r = zoneRect(zones, k);
    return r ? [r] : [];
  });
  const step = LAYOUT_GRID_MM;
  for (let y = frame.y + frame.h - want.h; y >= frame.y; y -= step) {
    for (let x = frame.x + frame.w - want.w; x >= frame.x; x -= step) {
      const cand = { x: snapMm(x), y: snapMm(y), w: want.w, h: want.h };
      if (!rectInside(cand, frame)) continue;
      if (rectsOverlap(cand, stamp)) continue;
      if (others.some((o) => rectsOverlap(cand, o))) continue;
      return cand;
    }
  }
  return null;
}

/* ── Превью подложки ↔ координаты листа ────────────────────────────────────── */

/** Пиксель превью (py — от верхнего края) → координаты листа (y вверх). */
export function previewToSheet(
  src: GeoSheetSource,
  px: number,
  py: number,
): { u: number; v: number } {
  return {
    u: src.extents[0] + px / src.px_per_unit,
    v: src.extents[3] - py / src.px_per_unit,
  };
}

export function sheetToPreview(
  src: GeoSheetSource,
  u: number,
  v: number,
): { px: number; py: number } {
  return {
    px: (u - src.extents[0]) * src.px_per_unit,
    py: (src.extents[3] - v) * src.px_per_unit,
  };
}

/* ── Привязка листа: прилипание к графике и автопривязка ──────────────────── */

/**
 * Что под точкой прилипания, по-русски. Без подписи на листе — общее имя; номер
 * сваи — «№12» (без двойного «№»). Вид, которого фронт не знает, — «точка».
 */
export function snapKindLabel(p: Pick<GeoSnapPoint, 'kind' | 'label'>): string {
  const label = p.label?.trim() ?? '';
  switch (p.kind) {
    case 'axis_cross':
      return label ? `пересечение осей ${label}` : 'пересечение осей';
    case 'line_cross':
      return 'пересечение линий';
    case 'pile': {
      const no = label.replace(/^№\s*/, '');
      return no ? `центр сваи №${no}` : 'центр сваи';
    }
    case 'circle':
      return label ? `центр окружности ${label}` : 'центр окружности';
    case 'block':
      return label ? `блок «${label}»` : 'блок';
    default:
      return label ? `точка ${label}` : 'точка';
  }
}

/** Источники элементов, извлечённых с этого же листа (`design/extract`). */
const SHEET_EXTRACTED_SOURCES: ReadonlySet<string> = new Set([
  'pdf_piles',
  'dxf',
  'outline',
  'assembly',
]);

/**
 * Годится ли проектный элемент опорой автопривязки по номерам: каталог РД и
 * ручной ввод — да; извлечённые с этого же листа — нет, их координаты посчитаны
 * через текущую привязку (замкнутый круг). Так же решает backend.
 */
export function isBindingReference(el: Pick<GeoDesignElement, 'source'>): boolean {
  return !SHEET_EXTRACTED_SOURCES.has(el.source);
}

const AUTO_POINT_PREFIX = 'auto:';

/** Контрольная точка, поставленная автопривязкой (`auto:<номер>`). */
export function isAutoControlPoint(id: string): boolean {
  return id.startsWith(AUTO_POINT_PREFIX);
}

/** Подпись контрольной точки: `auto:12` → «№12», остальные — как есть («Т3»). */
export function controlPointLabel(id: string): string {
  return isAutoControlPoint(id) ? `№${id.slice(AUTO_POINT_PREFIX.length)}` : id;
}

/* ── Реквизиты штампа по листу РД ─────────────────────────────────────────── */

export type GeoStampField = GeoStampSuggestion['field'];

/** Графы формы 3 ГОСТ Р 21.101 и чертёж-основание — в порядке формы шага 4. */
export const STAMP_FIELDS: ReadonlyArray<{ field: GeoStampField; label: string }> = [
  { field: 'designation', label: 'Обозначение документа (графа 1)' },
  { field: 'object_name', label: 'Объект строительства (графа 2)' },
  { field: 'structure_name', label: 'Здание, сооружение (графа 3)' },
  { field: 'sheet_title', label: 'Наименование изображения (графа 5)' },
  { field: 'stage', label: 'Стадия (графа 6)' },
  { field: 'organization', label: 'Организация (графа 9)' },
  { field: 'basis_drawing', label: 'Чертёж-основание' },
];

export function stampFieldLabel(field: GeoStampField): string {
  return STAMP_FIELDS.find((f) => f.field === field)?.label ?? field;
}

/** Масштаб рабочих чертежей, предлагаемый в интерфейсе (ГОСТ Р 51872 п.5.8). */
export const SCALE_OPTIONS: ReadonlyArray<number> = [20, 25, 50, 100, 200, 250, 500, 1000];

/** 1 pt PDF в метрах (1/72 дюйма). */
const PT_IN_M = 0.0254 / 72;

/** Для PDF-подложки: масштаб привязки s (м натуры на pt листа) → знаменатель 1:N. */
export function denominatorFromPdfScale(scale: number): number | null {
  if (!Number.isFinite(scale) || scale <= 0) return null;
  return scale / PT_IN_M;
}

/** Угол α осей здания по двум точкам одной оси (МСК): от направления на восток
 *  против часовой, градусы в (−180; 180]. */
export function axisAngleDeg(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number | null {
  const dE = b.y - a.y;
  const dN = b.x - a.x;
  if (Math.abs(dE) < 1e-9 && Math.abs(dN) < 1e-9) return null;
  return (Math.atan2(dN, dE) * 180) / Math.PI;
}

/** Есть ли среди людей штампа фамилия исполнителя (контроль ГОСТ п.5.6): строка
 *  «Геодезист», как в штампе ИС САТКОМ, или «Разработал» по ГОСТ — как на сервере. */
export function hasDeveloper(people: ReadonlyArray<{ role: string; name: string }>): boolean {
  return people.some((p) => /^\s*(разраб|геод)/i.test(p.role) && p.name.trim() !== '');
}

/** Уникальный id нового проектного элемента. */
export function newElementId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `manual-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
